import { useContext } from "react";
import { useUpsertCalendarReview } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { customEvent } from "src/utils/gtag.js";

/** Standard GA4 dimensions for Reviews-funnel events. */
export function buildReviewEventData(user, calendar) {
  return {
    user_id: user?._id,
    user_email: user?.email,
    calendar_id: calendar?._id,
    calendar_handle: calendar?.handle,
    calendar_name: calendar?.name,
  };
}

/**
 * Wrap useUpsertCalendarReview with snackbar feedback + GA4 customEvent
 * instrumentation. The 422 moderation case surfaces the server's
 * `error` + `field` so the dialog can highlight the offending input
 * instead of dumping a generic toast.
 */
export function useSubmitReview({ calendar } = {}) {
  const { user } = useContext(AuthContext) ?? {};
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  const mutation = useUpsertCalendarReview({
    onSuccess: (data, vars) => {
      const created = data?.status === 201;
      const starsOnly = !vars?.title?.trim() && !vars?.comment?.trim();
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        starsOnly
          ? created
            ? "Star rating saved!"
            : "Star rating updated!"
          : created
          ? "Review submitted!"
          : "Review updated!",
        "success",
      );
      customEvent({
        name: created ? "review_created" : "review_updated",
        category: "Reviews",
        label: "Calendar review upsert",
        data: buildReviewEventData(user, calendar),
      });
    },
    onError: (error) => {
      const status = error?.response?.status;
      const serverError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      if (status !== 422) {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          serverError || "Could not save your review. Please try again.",
          "error",
        );
      }
      customEvent({
        name: "review_submit_error",
        category: "Reviews",
        label: status === 422 ? "Moderation rejection" : "Generic error",
        data: { ...buildReviewEventData(user, calendar), status },
      });
    },
  });

  return {
    submitReview: (input) =>
      mutation.mutateAsync({ calendarId: calendar?._id, ...input }),
    isSubmitting: mutation.isLoading,
    error: mutation.error,
    reset: mutation.reset,
  };
}
